/**
 * @fileoverview Shared callable API contract for `beginTeachBackAttempt`
 * (#5270), which creates a learner's Teach it back attempt for a week. The
 * functions handler and the webapp client both import it, so the two sides
 * cannot drift in name, request shape, or response shape.
 */
import {z} from 'zod';

import {FirestoreDocumentIdSchema} from '../firebase/types';
import {
  TeachBackAttemptIdSchema,
  TeachBackIdSchema,
  TeachBackSectionWeekSchema,
} from './teachBack';
import type {TeachBackLockReason} from './teachBackRules';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * `sections` functions codebase.
 */
export const BEGIN_TEACH_BACK_ATTEMPT_FUNCTION_NAME = 'beginTeachBackAttempt';

/**
 * What the client sends. A learner names their Super Section; an admin
 * previewing a spec names the spec instead (`?teachBack=<id>` on the page).
 */
export const BeginTeachBackAttemptRequestSchema = z
  .object({
    /** The course. */
    courseId: FirestoreDocumentIdSchema,
    /** The 1-based section week. */
    sectionWeek: TeachBackSectionWeekSchema,
    /**
     * One id per Start click, reused by that click's retries; it becomes
     * the attempt's id.
     */
    requestId: TeachBackAttemptIdSchema,
    /**
     * The Super Section time slot whose week is taught: the learner's own, or
     * any of the course's for a student placed elsewhere. Required unless
     * previewing.
     */
    superSectionId: FirestoreDocumentIdSchema.optional(),
    /** An admin's preview of this spec. Omitted: a learner's attempt. */
    previewTeachBackId: TeachBackIdSchema.optional(),
  })
  .refine(
    request =>
      (request.superSectionId === undefined) !==
      (request.previewTeachBackId === undefined),
    'Name a Super Section or a spec to preview, not both',
  );

/** Parsed request. */
export type BeginTeachBackAttemptRequest = z.infer<
  typeof BeginTeachBackAttemptRequestSchema
>;

/** Why no attempt was created. */
export type BeginTeachBackAttemptRefusal =
  /** The named Super Section is not one of the course's, or has no schedule. */
  | 'unknown-super-section'
  /** The caller is not a student of the course. */
  | 'not-super-section-student'
  /** The week is locked; `lock` says why. */
  | 'locked'
  /** The course has no teach-back spec for the week (or the assigned one is gone). */
  | 'no-teach-back'
  /** An attempt for the week (this request's included) is in a session now. */
  | 'attempt-live'
  /** The week's charged attempts (sessions and uploads) reached the spec's cap. */
  | 'attempts-exhausted'
  /** This `requestId` made an attempt a session has since taken or ended. */
  | 'request-used';

/** What the callable returns. */
export type BeginTeachBackAttemptResponse =
  | {
      started: true;
      /** The attempt to connect a session to. */
      attemptId: string;
      /** The spec it teaches. */
      teachBackId: string;
      /** Charged attempts so far (this one included once it is charged). */
      attemptsUsed: number;
      /** The spec's cap. */
      maxAttempts: number;
    }
  | {
      started: false;
      /** Why not. */
      reason: BeginTeachBackAttemptRefusal;
      /** For `locked`: why. */
      lock?: TeachBackLockReason;
    };
